import { Eye, EyeOff, MoreVertical, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface PoolActionsMenuProps {
  isActive: boolean;
  canDelete: boolean;
  onSetInactive: () => void;
  onSetActive: () => void;
  onDelete: () => void;
}

// Admin-only - callers must gate rendering on hasPermission('canEditPools').
// The "Delete pool" item is further gated on canDelete
// (hasPermission('canDeletePools')), matching mobile's independent
// canDeletePools check.
const PoolActionsMenu = ({
  isActive,
  canDelete,
  onSetInactive,
  onSetActive,
  onDelete,
}: PoolActionsMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        title="More actions"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-grey3 bg-regular text-light hover:bg-cloud hover:text-primary"
      >
        <MoreVertical className="h-[19px] w-[19px]" />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[234px] rounded-2xl border border-grey3 bg-white p-1.5 shadow-[0_14px_40px_rgba(0,29,47,0.16)]">
          {isActive ? (
            <MenuItem
              icon={<EyeOff className="h-[18px] w-[18px]" />}
              label="Set pool to inactive"
              onClick={() => handleSelect(onSetInactive)}
            />
          ) : (
            <MenuItem
              icon={<Eye className="h-[18px] w-[18px]" />}
              label="Set pool to active"
              onClick={() => handleSelect(onSetActive)}
            />
          )}
          {canDelete && (
            <>
              <div className="my-1.5 h-px bg-grey3" />
              <MenuItem
                icon={<Trash2 className="h-[18px] w-[18px]" />}
                label="Delete pool"
                destructive
                onClick={() => handleSelect(onDelete)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  destructive?: boolean;
  onClick: () => void;
}

const MenuItem = ({ icon, label, destructive, onClick }: MenuItemProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium whitespace-nowrap hover:bg-cloud ${
      destructive
        ? 'text-danger [&>svg]:text-danger'
        : 'text-dark [&>svg]:text-grey10'
    }`}
  >
    {icon}
    {label}
  </button>
);

export default PoolActionsMenu;
